import {Alert, Box, Button, Modal, Typography} from '@mui/material';
import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import styles from '../../styles';
import {resumeShopifySubscription} from '../../api/membershipAPI';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  quantity?: number;
  shopifySubscriptionId?: number;
  onStart?: () => void;
  onSuccess?: () => Promise<void>;
  onError?: () => void;
}

const ResumeMembershipModal: React.FC<Props> = ({
  open,
  setOpen,
  quantity,
  shopifySubscriptionId,
  onStart,
  onSuccess,
  onError,
}) => {
  const {t} = useTranslation();

  const [pending, setPending] = useState<boolean>(false);

  const handleResumeShopifySubscription = useCallback(async () => {
    if (!shopifySubscriptionId) {
      alert(t('membership.unknownSubscription'));
      return;
    }
    setPending(true);
    if (onStart) {
      onStart();
    }
    const res = await resumeShopifySubscription({
      shopifySubscriptionId,
      pauseEndDate: new Date(),
    });
    setPending(false);
    if (res) {
      setOpen(false);
      if (onSuccess) {
        await onSuccess();
      }
    } else {
      if (onError) {
        onError();
      }
    }
  }, [shopifySubscriptionId, onStart, t, setOpen, onSuccess, onError]);

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={styles.modal}>
        <Typography id="modal-modal-title" variant="h4" sx={{mb: 2}}>
          {t('membership.resumeMembership')}
        </Typography>
        <ul>
          {(
            t('membership.resumeMembershipInstructions', {
              returnObjects: true,
            }) as string[]
          ).map(instruction => (
            <li key={instruction}>
              <Typography variant="body1">{instruction}</Typography>
            </li>
          ))}
        </ul>
        {quantity && quantity > 1 && (
          <Alert severity="warning" variant="outlined" sx={{mb: 2}}>
            {t('membership.thisWillAffectAllSubscriptions', {
              quantity,
            })}
          </Alert>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
          }}>
          <Button
            variant="contained"
            sx={{mr: 1}}
            onClick={handleResumeShopifySubscription}
            disabled={pending}>
            {t('membership.resumeMembership')}
          </Button>
          <Button variant="outlined" onClick={() => setOpen(false)}>
            {t('general.cancel')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ResumeMembershipModal;
